import { z } from 'zod'
import { baseEntitySchema } from '../../core/entity.js'

export const pollTypes = ['one-of-many', 'many-of-many'] as const
export type PollType = (typeof pollTypes)[number]

/**
 * The audio to play for a poll option. Hosted audio is stored by entity id only, because its signed URL expires;
 * the URL is fetched at play time. External links (Spotify, YouTube, …) keep the service and URL.
 */
export const pollOptionAudioSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('entity'),
    entityId: z.string().describe('Id of a hosted audio entity'),
  }),
  z.object({
    kind: z.literal('external'),
    service: z.string().describe('Embed service, e.g. spotify or youtube'),
    url: z.string(),
  }),
])

export type PollOptionAudio = z.infer<typeof pollOptionAudioSchema>

/**
 * A group of options with its own pick quota — e.g. an album whose best song is being chosen. Options point at a
 * group by `id`, never by position, so renaming or reordering groups moves no answers.
 */
export const pollGroupSchema = z.object({
  id: z.string().describe('Stable id; options point at it, so renaming or reordering a group moves no answers'),
  name: z.string(),
  quota: z.number().int().min(1).describe('Picks per respondent in this group. Absent means 1').optional(),
  removed: z.boolean().describe('Tombstone, same rule as options: never spliced, never un-set').optional(),
})

export type PollGroup = z.infer<typeof pollGroupSchema>

export const pollOptionSchema = z.object({
  name: z.string(),
  entityRef: z.string().optional(),
  audio: pollOptionAudioSchema.describe('Audio to play for this option, chosen with "Find audio"').optional(),
  start: z.iso.datetime().describe('Slot start as an ISO instant. Rendered in the viewer local zone').optional(),
  end: z.iso.datetime().describe('Slot end as an ISO instant').optional(),
  groupId: z.string().describe('The group this option belongs to, for grouped polls').optional(),
  removed: z
    .boolean()
    .describe('Tombstone. Responses are keyed by option index, so options are never spliced out')
    .optional(),
})

export type PollOption = z.infer<typeof pollOptionSchema>

/** A poll option that carries a date — the unit a rehearsal poll is built from. */
export type DateSlot = PollOption & { start: string }

export function isDateSlot(option: PollOption): option is DateSlot {
  return typeof option.start === 'string' && option.start.length > 0
}

export const pollSchema = baseEntitySchema.extend({
  type: z.literal('poll'),
  pollType: z.enum(pollTypes),
  name: z.string(),
  pollOptions: z.array(pollOptionSchema),
  pollGroups: z.array(pollGroupSchema).describe('Groups of options, each with its own pick quota').optional(),
  randomizeOptions: z.boolean().describe('Randomize the order of the poll options').optional(),
  invited: z.array(z.string()).optional(),
  closed: z.boolean().optional(),
  allowChange: z.boolean().optional(),

  // Note we do this here instead of extended types because we cannot merge a union schema
  responseQuota: z.number().optional(),

  /**
   * How many slots the owner needs to pick at finalize. A separate axis from `responseQuota`,
   * which caps how many options a *respondent* may choose.
   */
  datesNeeded: z
    .number()
    .int()
    .min(1)
    .describe('How many slots the owner needs to pick at finalize. Absent means 1')
    .optional(),
  respondBy: z.iso.datetime().describe('Advisory deadline shown to respondents. Never auto-closes the poll').optional(),

  /**
   * Where the winning slots were written, once they have been. Set only after the create
   * resolves, so it is a record of a completed handoff rather than an intent to make one —
   * which is what lets the closed page show `Added to Band calendar` instead of offering to
   * do it again and quietly duplicating every event.
   */
  calendarId: z
    .string()
    .describe('Calendar the winning slots were added to. Absent until the handoff has completed')
    .optional(),

  // specified in the extended types
  finalOption: z.unknown().optional(),
})

export const oneOfManyPollSchema = pollSchema.extend({
  pollType: z.literal('one-of-many'),
  finalOption: z.number().optional(),
})
export type OneOfManyPoll = z.infer<typeof oneOfManyPollSchema>

export const manyOfManyPollSchema = pollSchema.extend({
  pollType: z.literal('many-of-many'),
  finalOption: z.record(z.string(), z.boolean()).optional(),
})
export type ManyOfManyPoll = z.infer<typeof manyOfManyPollSchema>

export type Poll = z.infer<typeof pollSchema>

export function isOneOfManyPoll(node?: unknown): node is OneOfManyPoll {
  return oneOfManyPollSchema.safeParse(node).success
}

export function isManyOfManyPoll(node?: unknown): node is ManyOfManyPoll {
  return manyOfManyPollSchema.safeParse(node).success
}

export function isPoll(node?: unknown): node is Poll {
  return pollSchema.safeParse(node).success
}

/**
 * A rehearsal poll is a many-of-many poll with dated slots — deliberately NOT a new pollType.
 * `finalOption` on many-of-many is already `Record<string, boolean>`, so N winners need no new
 * representation, and a one-of-many poll could never express N > 1.
 *
 * Tombstones count. Identity reads the append-only history — "has this poll ever had a dated
 * slot?" — never the mutable set of live options. When this also required a live slot, removing
 * the last date flipped the poll back to a legacy text poll: the owner got the `Create option`
 * editor with no date pickers and no `Add another date`, so the poll was bricked by ordinary
 * use with no route back. Options are never spliced and `removed` is never cleared, so this
 * predicate is monotonic — which is what makes it safe to route a capability on.
 *
 * Removing every date stays legal; it renders the slot list's empty state, and the owner adds
 * a new one from there.
 */
export function isDatePoll(node?: unknown): node is ManyOfManyPoll {
  return isManyOfManyPoll(node) && node.pollOptions.some(isDateSlot)
}

export type GroupedPoll = ManyOfManyPoll & { pollGroups: PollGroup[] }

/**
 * A grouped poll is a many-of-many poll whose options belong to groups — deliberately NOT a new pollType, for the
 * same reason as `isDatePoll`: responses and `finalOption` stay keyed by option index. Removed groups count, so
 * deleting every group never turns the poll back into a plain poll.
 */
export function isGroupedPoll(node?: unknown): node is GroupedPoll {
  return isManyOfManyPoll(node) && Array.isArray(node.pollGroups) && node.pollGroups.length > 0
}
